/**
 * Dashboard Context
 *
 * Manages:
 * - Widget layout state and persistence (localStorage + backend)
 * - Auto-save on layout changes (debounced)
 * - Real-time metrics stream data
 * - Widget configuration
 * - User preferences
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useMemo, useRef } from "react";
import { AuthContext } from "context/AuthContext";
import useApi from "hooks/useApi";
import { endpoints } from "config/api";
import useMetrics from "hooks/useMetrics";
import { DEFAULT_LAYOUT, getWidgetById, DEFAULT_SIZES, mergeBackendRegistry, getDynamicRegistry, getDynamicWidgetById } from "../config/widgetRegistry";

const STORAGE_KEY_PREFIX = "kadmin_dashboard_layout_";
const DEBOUNCE_SAVE_MS = 1500;

// ==================== ACTION TYPES ====================
const ACTIONS_TYPES = {
  SET_LAYOUT: "SET_LAYOUT",
  UPDATE_LAYOUT: "UPDATE_LAYOUT",
  ADD_WIDGET: "ADD_WIDGET",
  REMOVE_WIDGET: "REMOVE_WIDGET",
  SET_WIDGET_CONFIG: "SET_WIDGET_CONFIG",
  SET_WIDGET_CONFIGS: "SET_WIDGET_CONFIGS",
  SET_EXPANDED_WIDGET: "SET_EXPANDED_WIDGET",
  SET_EDITING_MODE: "SET_EDITING_MODE",
  SET_ACCESSIBLE_CATEGORIES: "SET_ACCESSIBLE_CATEGORIES",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  SET_TIME_RANGE: "SET_TIME_RANGE",
  RESET_LAYOUT: "RESET_LAYOUT",
  SET_WIDGET_SELECTOR_OPEN: "SET_WIDGET_SELECTOR_OPEN",
  SET_SAVING: "SET_SAVING",
  SET_CURRENT_LAYOUT_NAME: "SET_CURRENT_LAYOUT_NAME",
  SET_SAVED_LAYOUTS: "SET_SAVED_LAYOUTS",
  SET_WIDGET_FILTERS: "SET_WIDGET_FILTERS",
  SET_REGISTRY_LOADED: "SET_REGISTRY_LOADED",
};

// ==================== INITIAL STATE ====================
const initialState = {
  layout: [],
  originalLayout: null,
  currentLayoutName: null,
  savedLayouts: [],
  isEditingMode: false,
  isSaving: false,
  activeWidgets: [],
  widgetConfigs: {},
  expandedWidget: null,
  accessibleCategories: [],
  loading: true,
  error: null,
  timeRange: {
    start: null,
    end: null,
    preset: "7d",
  },
  widgetSelectorOpen: false,
  registryLoaded: false,
};

// ==================== REDUCER ====================
function dashboardReducer(state, action) {
  switch (action.type) {
    case ACTIONS_TYPES.SET_LAYOUT:
      return {
        ...state,
        layout: action.payload,
        activeWidgets: action.payload.map((item) => item.i),
        loading: false,
      };

    case ACTIONS_TYPES.UPDATE_LAYOUT:
      return {
        ...state,
        layout: action.payload,
        activeWidgets: action.payload.map((item) => item.i),
      };

    case ACTIONS_TYPES.ADD_WIDGET: {
      const { widgetId, position } = action.payload;
      const widget = getWidgetById(widgetId);
      if (!widget) return state;

      const size = DEFAULT_SIZES[widget.type] || { w: 3, h: 2, minW: 2, minH: 2 };

      const newLayoutItem = {
        i: widgetId,
        x: position?.x ?? 0,
        y: position?.y ?? Infinity,
        w: size.w,
        h: size.h,
        minW: size.minW,
        minH: size.minH,
      };

      const newLayout = [...state.layout, newLayoutItem];
      return {
        ...state,
        layout: newLayout,
        activeWidgets: newLayout.map((item) => item.i),
      };
    }

    case ACTIONS_TYPES.REMOVE_WIDGET: {
      const newLayout = state.layout.filter((item) => item.i !== action.payload);
      return {
        ...state,
        layout: newLayout,
        activeWidgets: newLayout.map((item) => item.i),
      };
    }

    case ACTIONS_TYPES.SET_WIDGET_CONFIG:
      return {
        ...state,
        widgetConfigs: {
          ...state.widgetConfigs,
          [action.payload.widgetId]: {
            ...(state.widgetConfigs[action.payload.widgetId] || {}),
            ...action.payload.config,
          },
        },
      };

    case ACTIONS_TYPES.SET_EXPANDED_WIDGET:
      return { ...state, expandedWidget: action.payload };

    case ACTIONS_TYPES.SET_EDITING_MODE:
      return {
        ...state,
        isEditingMode: action.payload,
        originalLayout: action.payload ? [...state.layout] : state.originalLayout,
      };

    case ACTIONS_TYPES.SET_ACCESSIBLE_CATEGORIES:
      return { ...state, accessibleCategories: Array.isArray(action.payload) ? action.payload : [] };

    case ACTIONS_TYPES.SET_LOADING:
      return { ...state, loading: action.payload };

    case ACTIONS_TYPES.SET_SAVING:
      return { ...state, isSaving: action.payload };

    case ACTIONS_TYPES.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case ACTIONS_TYPES.SET_TIME_RANGE: {
      const newTimeRange = { ...action.payload };
      if (newTimeRange.start && typeof newTimeRange.start === "string") {
        newTimeRange.start = new Date(newTimeRange.start);
      }
      if (newTimeRange.end && typeof newTimeRange.end === "string") {
        newTimeRange.end = new Date(newTimeRange.end);
      }
      return { ...state, timeRange: newTimeRange };
    }

    case ACTIONS_TYPES.RESET_LAYOUT: {
      const resetTo = state.originalLayout || action.payload || DEFAULT_LAYOUT;
      return {
        ...state,
        layout: resetTo,
        activeWidgets: resetTo.map((item) => item.i),
        isEditingMode: false,
      };
    }

    case ACTIONS_TYPES.SET_WIDGET_SELECTOR_OPEN:
      return { ...state, widgetSelectorOpen: action.payload };

    case ACTIONS_TYPES.SET_CURRENT_LAYOUT_NAME:
      return { ...state, currentLayoutName: action.payload };

    case ACTIONS_TYPES.SET_SAVED_LAYOUTS:
      return { ...state, savedLayouts: Array.isArray(action.payload) ? action.payload : [] };

    case ACTIONS_TYPES.SET_WIDGET_FILTERS:
      return {
        ...state,
        widgetConfigs: {
          ...state.widgetConfigs,
          [action.payload.widgetId]: {
            ...(state.widgetConfigs[action.payload.widgetId] || {}),
            filters: action.payload.filters,
          },
        },
      };

    case ACTIONS_TYPES.SET_WIDGET_CONFIGS:
      return { ...state, widgetConfigs: action.payload || {} };

    case ACTIONS_TYPES.SET_REGISTRY_LOADED:
      return { ...state, registryLoaded: action.payload };

    default:
      return state;
  }
}

// ==================== CONTEXT ====================
const DashboardContext = createContext({
  state: initialState,
  dispatch: () => {},
  metricsData: {},
  streamConnected: false,
  streamError: null,
  actions: {},
});

// ==================== PROVIDER ====================
export function DashboardProvider({ children }) {
  const [state, dispatch] = useReducer(dashboardReducer, initialState);
  const { currentUser: user } = useContext(AuthContext);
  const { get, post } = useApi();

  const getRef = useRef(get);
  const postRef = useRef(post);
  const saveTimeoutRef = useRef(null);
  const lastSavedLayoutRef = useRef(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    getRef.current = get;
    postRef.current = post;
  }, [get, post]);

  // ==================== FETCH DYNAMIC REGISTRY ====================
  useEffect(() => {
    const fetchRegistry = async () => {
      if (!user?.uid) return;
      try {
        const response = await getRef.current(endpoints.metrics.registry);
        if (response?.metrics) {
          mergeBackendRegistry(response.metrics, response.categories);
          dispatch({ type: ACTIONS_TYPES.SET_REGISTRY_LOADED, payload: true });
        }
      } catch (_err) {
        // Registry unavailable — use static defaults
        dispatch({ type: ACTIONS_TYPES.SET_REGISTRY_LOADED, payload: true });
      }
    };
    fetchRegistry();
  }, [user?.uid]);

  // ==================== LOAD LAYOUT ====================
  useEffect(() => {
    const loadLayout = async () => {
      if (!user?.uid) return;

      dispatch({ type: ACTIONS_TYPES.SET_LOADING, payload: true });

      try {
        // Try backend
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);

          const response = await getRef
            .current(endpoints.metrics.layout, { signal: controller.signal })
            .catch((err) => {
              if (err.name === "AbortError") return null;
              throw err;
            })
            .finally(() => clearTimeout(timeoutId));

          if (response?.savedLayouts && Array.isArray(response.savedLayouts)) {
            dispatch({ type: ACTIONS_TYPES.SET_SAVED_LAYOUTS, payload: response.savedLayouts });
          }

          if (response?.layout && Array.isArray(response.layout) && response.layout.length > 0) {
            dispatch({ type: ACTIONS_TYPES.SET_LAYOUT, payload: response.layout });
            if (response.timeRange) {
              dispatch({ type: ACTIONS_TYPES.SET_TIME_RANGE, payload: response.timeRange });
            }
            if (response.widgetConfigs) {
              dispatch({ type: ACTIONS_TYPES.SET_WIDGET_CONFIGS, payload: response.widgetConfigs });
            }
            const cacheData = { layout: response.layout, timeRange: response.timeRange, widgetConfigs: response.widgetConfigs };
            localStorage.setItem(`${STORAGE_KEY_PREFIX}${user.uid}`, JSON.stringify(cacheData));
            lastSavedLayoutRef.current = JSON.stringify(cacheData);
            isInitializedRef.current = true;
            return;
          }
        } catch (fetchErr) {
          // Backend not available, fall through
        }

        // Try localStorage cache
        const cachedData = localStorage.getItem(`${STORAGE_KEY_PREFIX}${user.uid}`);
        if (cachedData) {
          try {
            const parsed = JSON.parse(cachedData);
            const layout = Array.isArray(parsed) ? parsed : parsed.layout;
            const timeRange = Array.isArray(parsed) ? null : parsed.timeRange;
            const widgetConfigs = Array.isArray(parsed) ? null : parsed.widgetConfigs;

            if (Array.isArray(layout) && layout.length > 0) {
              dispatch({ type: ACTIONS_TYPES.SET_LAYOUT, payload: layout });
              if (timeRange) dispatch({ type: ACTIONS_TYPES.SET_TIME_RANGE, payload: timeRange });
              if (widgetConfigs) dispatch({ type: ACTIONS_TYPES.SET_WIDGET_CONFIGS, payload: widgetConfigs });
              lastSavedLayoutRef.current = cachedData;
              isInitializedRef.current = true;
              return;
            }
          } catch (_e) {
            // corrupt cache, fall through
          }
        }

        // Default layout
        dispatch({ type: ACTIONS_TYPES.SET_LAYOUT, payload: DEFAULT_LAYOUT });
        lastSavedLayoutRef.current = JSON.stringify(DEFAULT_LAYOUT);
        isInitializedRef.current = true;
      } catch (err) {
        dispatch({ type: ACTIONS_TYPES.SET_LAYOUT, payload: DEFAULT_LAYOUT });
        isInitializedRef.current = true;
      }
    };

    loadLayout();
  }, [user?.uid]);

  // ==================== AUTO-SAVE LAYOUT ====================
  const saveLayoutToBackend = useCallback(
    async (layoutToSave, timeRangeToSave, widgetConfigsToSave, layoutName = null) => {
      if (!user?.uid) return;

      const configToSave = { layout: layoutToSave, timeRange: timeRangeToSave, widgetConfigs: widgetConfigsToSave, layoutName };
      const configJson = JSON.stringify(configToSave);

      if (!layoutName && configJson === lastSavedLayoutRef.current) return;

      dispatch({ type: ACTIONS_TYPES.SET_SAVING, payload: true });

      try {
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${user.uid}`, configJson);

        const savePayload = { layout: layoutToSave, timeRange: timeRangeToSave, widgetConfigs: widgetConfigsToSave };
        if (layoutName) {
          savePayload.layoutName = layoutName;
          savePayload.saveAsNamed = true;
        }

        await postRef.current(endpoints.metrics.saveLayout, savePayload);
        lastSavedLayoutRef.current = configJson;
      } catch (_err) {
        // Still saved in localStorage
      } finally {
        dispatch({ type: ACTIONS_TYPES.SET_SAVING, payload: false });
      }
    },
    [user?.uid]
  );

  // Watch for layout or timeRange changes and trigger auto-save
  useEffect(() => {
    if (!isInitializedRef.current || state.loading) return;

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(() => {
      saveLayoutToBackend(state.layout, state.timeRange, state.widgetConfigs, state.currentLayoutName);
    }, DEBOUNCE_SAVE_MS);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [state.layout, state.timeRange, state.widgetConfigs, state.currentLayoutName, state.loading, saveLayoutToBackend]);

  // Set accessible categories (for KADMIN, all categories are accessible by default)
  useEffect(() => {
    if (!user?.uid) return;
    // Default: all categories accessible
    const defaultCategories = [
      { category: "users" },
      { category: "samples" },
      { category: "system" },
    ];
    dispatch({ type: ACTIONS_TYPES.SET_ACCESSIBLE_CATEGORIES, payload: defaultCategories });
  }, [user?.uid]);

  // Connect to metrics stream
  const accessibleCategoriesList = Array.isArray(state.accessibleCategories) ? state.accessibleCategories : [];

  const {
    data: metricsData,
    connected: streamConnected,
    error: streamError,
    lastUpdate,
  } = useMetrics({
    categories: accessibleCategoriesList.map((c) => c.category),
    timeRange: state.timeRange?.preset || "7d",
    autoConnect: !!user?.uid && accessibleCategoriesList.length > 0,
  });

  // ==================== ACTIONS ====================
  const actions = useMemo(
    () => ({
      updateLayout: (newLayout) => {
        dispatch({ type: ACTIONS_TYPES.UPDATE_LAYOUT, payload: newLayout });
      },

      addWidget: (widgetId, position) => {
        const widget = getWidgetById(widgetId);
        if (!widget) return false;
        dispatch({ type: ACTIONS_TYPES.ADD_WIDGET, payload: { widgetId, position } });
        return true;
      },

      removeWidget: (widgetId) => {
        dispatch({ type: ACTIONS_TYPES.REMOVE_WIDGET, payload: widgetId });
      },

      resetToDefault: () => {
        dispatch({ type: ACTIONS_TYPES.RESET_LAYOUT, payload: DEFAULT_LAYOUT });
      },

      setWidgetConfig: (widgetId, config) => {
        dispatch({ type: ACTIONS_TYPES.SET_WIDGET_CONFIG, payload: { widgetId, config } });
      },

      expandWidget: (widgetId) => {
        dispatch({ type: ACTIONS_TYPES.SET_EXPANDED_WIDGET, payload: widgetId });
      },

      collapseWidget: () => {
        dispatch({ type: ACTIONS_TYPES.SET_EXPANDED_WIDGET, payload: null });
      },

      enterEditMode: () => {
        dispatch({ type: ACTIONS_TYPES.SET_EDITING_MODE, payload: true });
      },

      exitEditMode: (save = true) => {
        if (!save && state.originalLayout) {
          dispatch({ type: ACTIONS_TYPES.RESET_LAYOUT });
        } else {
          dispatch({ type: ACTIONS_TYPES.SET_EDITING_MODE, payload: false });
        }
      },

      setTimeRange: (range) => {
        dispatch({ type: ACTIONS_TYPES.SET_TIME_RANGE, payload: range });
      },

      saveNamedLayout: async (name) => {
        if (!user?.uid || !name) return false;
        try {
          await postRef.current(endpoints.metrics.saveLayout, {
            layout: state.layout,
            timeRange: state.timeRange,
            layoutName: name,
            saveAsNamed: true,
          });
          dispatch({ type: ACTIONS_TYPES.SET_CURRENT_LAYOUT_NAME, payload: name });

          const existingIndex = state.savedLayouts.findIndex((l) => l.name === name);
          const newSavedLayout = { name, layout: state.layout, timeRange: state.timeRange, savedAt: new Date().toISOString() };
          const updatedLayouts = [...state.savedLayouts];
          if (existingIndex >= 0) {
            updatedLayouts[existingIndex] = newSavedLayout;
          } else {
            updatedLayouts.push(newSavedLayout);
          }
          dispatch({ type: ACTIONS_TYPES.SET_SAVED_LAYOUTS, payload: updatedLayouts });
          return true;
        } catch (_err) {
          return false;
        }
      },

      loadSavedLayout: (name) => {
        const savedLayout = state.savedLayouts.find((l) => l.name === name);
        if (!savedLayout) return false;
        dispatch({ type: ACTIONS_TYPES.SET_LAYOUT, payload: savedLayout.layout });
        if (savedLayout.timeRange) dispatch({ type: ACTIONS_TYPES.SET_TIME_RANGE, payload: savedLayout.timeRange });
        dispatch({ type: ACTIONS_TYPES.SET_CURRENT_LAYOUT_NAME, payload: name });
        return true;
      },

      deleteLayout: async (name) => {
        if (!user?.uid || !name) return false;
        const updatedLayouts = state.savedLayouts.filter((l) => l.name !== name);
        dispatch({ type: ACTIONS_TYPES.SET_SAVED_LAYOUTS, payload: updatedLayouts });
        if (state.currentLayoutName === name) {
          dispatch({ type: ACTIONS_TYPES.SET_CURRENT_LAYOUT_NAME, payload: null });
        }
        return true;
      },

      setCurrentLayoutName: (name) => {
        dispatch({ type: ACTIONS_TYPES.SET_CURRENT_LAYOUT_NAME, payload: name });
      },

      setWidgetFilters: (widgetId, filters) => {
        dispatch({ type: ACTIONS_TYPES.SET_WIDGET_FILTERS, payload: { widgetId, filters } });
      },

      getWidgetFilters: (widgetId) => {
        return state.widgetConfigs[widgetId]?.filters || {};
      },

      openWidgetSelector: () => {
        dispatch({ type: ACTIONS_TYPES.SET_WIDGET_SELECTOR_OPEN, payload: true });
      },

      closeWidgetSelector: () => {
        dispatch({ type: ACTIONS_TYPES.SET_WIDGET_SELECTOR_OPEN, payload: false });
      },

      canAccessWidget: (widgetId) => {
        const widget = getDynamicWidgetById(widgetId) || getWidgetById(widgetId);
        if (!widget) return false;
        return accessibleCategoriesList.some((cat) => cat.category === widget.category);
      },

      getWidgetData: (widgetId) => {
        const widget = getDynamicWidgetById(widgetId) || getWidgetById(widgetId);
        if (!widget) return null;
        const categoryData = metricsData[widget.category];
        if (!categoryData) return null;
        return categoryData[widget.dataKey] ?? categoryData;
      },

      refreshData: async () => {
        // Stream hook handles reconnection
      },

      forceSave: () => {
        saveLayoutToBackend(state.layout, state.timeRange, state.widgetConfigs);
      },
    }),
    [accessibleCategoriesList, state.layout, state.originalLayout, state.savedLayouts, state.currentLayoutName, state.timeRange, state.widgetConfigs, metricsData, saveLayoutToBackend, user]
  );

  // ==================== CONTEXT VALUE ====================
  const contextValue = useMemo(
    () => ({
      state,
      dispatch,
      metricsData,
      streamConnected,
      streamError,
      lastUpdate,
      actions,
      getDynamicRegistry,
      getDynamicWidgetById,
    }),
    [state, metricsData, streamConnected, streamError, lastUpdate, actions]
  );

  return <DashboardContext.Provider value={contextValue}>{children}</DashboardContext.Provider>;
}

// ==================== HOOK ====================
export function useDashboard() {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error("useDashboard must be used within a DashboardProvider");
  }
  return context;
}

export { ACTIONS_TYPES };
export default DashboardContext;
